import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios";
import { uploadImageToS3, deleteImageFromS3, getFileExtension } from "../../utils/s3Upload";
import { getRole } from "../../utils/role";

const FollowUpStage3 = ({ reportId, initialData = null, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState({});

  const [formData, setFormData] = useState({
    // Q1: Applied First Dose?
    applied_first_dose: "Yes",
    first_dose_date: "",
    first_dose_reason: "",

    // Q2: Applied Second Dose?
    applied_second_dose: "Yes",
    second_dose_date: "",
    second_dose_reason: "",

    // Q3: Following Soil Doctor Recommendation?
    follow_soil_doctor: "Yes",
    soil_doctor_reason: "",

    // Q4: Disease / Pest Progress Since Last Follow-up
    pest_progress_status: "Resolved",
    pest_progress_details: "",
    pest_progress_urls: [],

    // Q6: Overall Crop Progress vs Expectation
    crop_progress_status: "On Track",
    crop_progress_urls: [],

    // General Remarks
    remarks: ""
  });

  const isReadOnly = Boolean(initialData && Object.keys(initialData).length > 0) && getRole() !== 'SUPPORT';

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const sanitizedData = { ...initialData };

      const urlKeys = ["pest_progress_urls", "crop_progress_urls"];
      urlKeys.forEach((key) => {
        if (typeof sanitizedData[key] === "string") {
          sanitizedData[key] = [sanitizedData[key]];
        } else if (!Array.isArray(sanitizedData[key])) {
          sanitizedData[key] = [];
        }
      });

      setFormData(sanitizedData);
    }
    setLoading(false);
  }, [initialData]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };


  const handleMultiPhotoUpload = async (fieldKey, fileList) => {
    if (isReadOnly || !fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    try {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: true }));
      
      const uploadPromises = files.map(async (file) => {
        const fileExt = getFileExtension(file);
        return await uploadImageToS3(file, fileExt, "follow-up-photos");
      });

      const newS3Urls = await Promise.all(uploadPromises);
      const existingUrls = formData[fieldKey] || [];
      handleChange(fieldKey, [...existingUrls, ...newS3Urls]);
    } catch (err) {
      console.error("S3 Multi-Upload Error:", err);
      alert("Failed to upload photos to AWS S3. Please check your network.");
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handlePhotoRemove = async (fieldKey, indexToRemove) => {
    if (isReadOnly) return;
    const urlsArray = formData[fieldKey] || [];
    const fileUrl = urlsArray[indexToRemove];
    if (!fileUrl) return;

    try {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: true }));
      await deleteImageFromS3(fileUrl);
      
      const updatedUrls = urlsArray.filter((_, i) => i !== indexToRemove);
      handleChange(fieldKey, updatedUrls);
    } catch (err) {
      console.error("S3 Deletion Error:", err);
      alert("Could not delete photo from AWS server.");
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    
    if (Object.values(uploadingMedia).some((up) => up)) {
      alert("Please wait for all photo uploads to finish before submitting.");
      return;
    }


    try {
      setSubmitting(true);
      await axiosInstance.post(`/follow-up/response/${reportId}`, {
        follow_up_stage: 3,
        answers: formData
      });

      alert("Stage 3 Mid-to-Late Season Progress submitted successfully!");
      if (onSuccess) onSuccess();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit Stage 3.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderMediaWidget = (fieldKey, label, isRequired = false) => {
    const urls = formData[fieldKey] || [];
    const isUploading = uploadingMedia[fieldKey] || false;

    return (
      <div className={`mt-2.5 pt-2.5 border-t border-dashed rounded-md p-3 ${
        isRequired && urls.length === 0 ? "border-amber-300 bg-amber-50/40" : "border-gray-200 bg-purple-50/30"
      }`}>
        <label className="block text-xs font-semibold text-purple-900 mb-1.5">
          {label} {isRequired && <span className="text-red-500 font-bold">*</span>}
        </label>
        
        {!isReadOnly && (
          <input
            type="file"
            multiple
            accept="image/*,.pdf"
            disabled={isUploading}
            onChange={(e) => handleMultiPhotoUpload(fieldKey, e.target.files)}
            className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-purple-100 file:text-purple-700 file:font-semibold hover:file:bg-purple-200 cursor-pointer disabled:opacity-50"
          />
        )}

        {isUploading && (
          <div className="text-xs text-purple-600 font-medium mt-2 flex items-center gap-2">
            <span className="inline-block w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></span>
            Uploading media to AWS S3...
          </div>
        )}

        {urls.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {urls.map((url, idx) => (
              <div key={idx} className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded border border-purple-200 text-xs shadow-sm max-w-full">
                <span className="text-green-600 font-bold shrink-0">✓</span>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline truncate max-w-[140px] sm:max-w-[200px]"
                >
                  {url.split("/").pop()}
                </a>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => handlePhotoRemove(fieldKey, idx)}
                    className="text-red-500 hover:text-red-700 font-bold ml-1 p-0.5 shrink-0"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="p-6 text-center text-gray-500 text-sm">Loading Stage 3 form...</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 text-left">

      {isReadOnly && (
        <div className="bg-blue-50 text-blue-800 text-xs sm:text-sm p-3 rounded-md border border-blue-200 font-medium flex items-center gap-2">
          🔒 This form has already been submitted and is locked for editing.
        </div>
      )}
      
      {/* SECTION 1: DOSE COMPLIANCE & SOIL DOCTOR ADHERENCE */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-5">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">
          1. Dose Compliance & Soil Doctor Adherence
        </h4>

        {/* Q1: Applied First Dose? */}
        <div className="space-y-2">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            1. Applied First Dose (per advisory)? <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={formData.applied_first_dose}
              onChange={(e) => handleChange("applied_first_dose", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>

            {formData.applied_first_dose === "Yes" ? (
              <div className="sm:col-span-2 flex items-center gap-2">
                <span className="text-xs text-gray-500 shrink-0">Date Applied:</span>
                <input
                  type="date"
                  value={formData.first_dose_date}
                  onChange={(e) => handleChange("first_dose_date", e.target.value)}
                  required
                  disabled={isReadOnly}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
                />
              </div>
            ) : (
              <input
                type="text"
                value={formData.first_dose_reason}
                onChange={(e) => handleChange("first_dose_reason", e.target.value)}
                required
                disabled={isReadOnly}
                placeholder="Reason: Why was the first dose skipped / delayed?"
                className="w-full sm:col-span-2 border border-amber-300 bg-amber-50/20 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
              />
            )}
          </div>
        </div>

        {/* Q2: Applied Second Dose? */}
        <div className="space-y-2 pt-3 border-t border-gray-100">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            2. Applied Second Dose? <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={formData.applied_second_dose}
              onChange={(e) => handleChange("applied_second_dose", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>

            {formData.applied_second_dose === "Yes" ? (
              <div className="sm:col-span-2 flex items-center gap-2">
                <span className="text-xs text-gray-500 shrink-0">Date Applied:</span>
                <input
                  type="date"
                  value={formData.second_dose_date}
                  onChange={(e) => handleChange("second_dose_date", e.target.value)}
                  required
                  disabled={isReadOnly}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
                />
              </div>
            ) : (
              <input
                type="text"
                value={formData.second_dose_reason}
                onChange={(e) => handleChange("second_dose_reason", e.target.value)}
                required
                disabled={isReadOnly}
                placeholder="Reason: Why was the second dose skipped / delayed?"
                className="w-full sm:col-span-2 border border-amber-300 bg-amber-50/20 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
              />
            )}
          </div>
        </div>

        {/* Q3: Following Soil Doctor Recommendation? */}
        <div className="space-y-2 pt-3 border-t border-gray-100">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            3. Following Soil Doctor Recommendation? <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={formData.follow_soil_doctor}
              onChange={(e) => handleChange("follow_soil_doctor", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="Yes">Yes</option>
              <option value="Partially">Partially</option>
              <option value="No">No (Specify Reason)</option>
            </select>

            {formData.follow_soil_doctor !== "Yes" && (
              <input
                type="text"
                value={formData.soil_doctor_reason}
                onChange={(e) => handleChange("soil_doctor_reason", e.target.value)}
                required
                disabled={isReadOnly}
                placeholder="Specify what part was skipped or why recommendations weren't followed..."
                className="w-full sm:col-span-2 border border-amber-300 bg-amber-50/20 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
              />
            )}
          </div>
        </div>
      </div>

      {/* SECTION 2: DISEASE PROGRESS & INVESTMENT */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-5">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">
          2. Disease Progress & Advisory-Period Investment
        </h4>

        {/* Q4: Disease / Pest Progress */}
        <div className="space-y-2">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            4. Disease / Pest Progress Since Last Follow-up <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={formData.pest_progress_status}
              onChange={(e) => handleChange("pest_progress_status", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="Resolved">Resolved</option>
              <option value="Improved">Improved</option>
              <option value="Same">Same</option>
              <option value="Worsened">Worsened</option>
              <option value="New Issue">New Issue</option>
            </select>
            <input
              type="text"
              value={formData.pest_progress_details}
              onChange={(e) => handleChange("pest_progress_details", e.target.value)}
              disabled={isReadOnly}
              placeholder="Details: Describe current symptoms, recovery, or new pest attack..."
              className="w-full sm:col-span-2 border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          {renderMediaWidget("pest_progress_urls", "📷 Optional: Upload Photo of Disease Progress / Pest Recovery", false)}
        </div>
      </div>

      {/* SECTION 3: OVERALL CROP PROGRESS */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">
          3. Overall Progress vs Expectation
        </h4>

        {/* Q5: Overall Crop Progress vs Expectation */}
        <div className="space-y-2">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            5. Overall Crop Progress vs Expectation <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.crop_progress_status}
            onChange={(e) => handleChange("crop_progress_status", e.target.value)}
            disabled={isReadOnly}
            className="w-full sm:w-1/2 border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
          >
            <option value="On Track">On Track</option>
            <option value="Delayed">Delayed</option>
            <option value="At Risk">At Risk</option>
          </select>
          {renderMediaWidget("crop_progress_urls", "📷 Optional: Upload Overall Field Progress Photo", false)}
        </div>
      </div>

      {/* SECTION 4: REMARKS */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm">
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
          General Remarks / Additional Field Notes
        </label>
        <textarea
          rows={3}
          value={formData.remarks}
          onChange={(e) => handleChange("remarks", e.target.value)}
          disabled={isReadOnly}
          placeholder="Add any general observations, farmer feedback, or summary remarks..."
          className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
        />
      </div>

      {/* Submit Button */}
      {!isReadOnly && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={submitting || Object.values(uploadingMedia).some((up) => up)}
            className="w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-md hover:bg-purple-700 disabled:opacity-50 transition shadow-sm"
          >
            {submitting ? "Saving Stage 3..." : initialData ? "Update Stage 3" : "Submit Stage 3"}
          </button>
        </div>
      )}
    </form>
  );
};

export default FollowUpStage3;